import React from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import Tag from '@atlaskit/tag';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import { useBespokeEmptyFieldTextExperiment } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-experiment/index.tsx';
import { ColoredTag } from '@atlassian/jira-option-color-picker/src/colored-tag/index.tsx';
import { memoizeGetAutoGeneratedColorForLabelsTag } from '@atlassian/jira-option-color-picker/src/option-color-generator/index.tsx';
import type { labels_issueFieldLabelsReadviewFull_LabelsReadView$key as LabelReadViewFragment } from '@atlassian/jira-relay/src/__generated__/labels_issueFieldLabelsReadviewFull_LabelsReadView.graphql';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { getColorMetadata } from '@atlassian/jira-option-color-picker/src/color-picker/utils/index.tsx';
import { getLabelHref } from '../../services/transform-strings/utils.tsx';
import { LabelsTruncatedReadView } from './truncated/index.tsx';
import messages from './truncated/messages.tsx';
import type { LabelsReadViewProps } from './types.tsx';

/**
 * The LabelsReadView will show a read only view of labels.
 * @param props {@link LabelsReadViewProps}
 */
export const LabelsReadView = ({
	baseSearchUrl,
	fragmentRef,
	isTruncated,
	isHoverPopoverEnabled,
}: LabelsReadViewProps) => {
	const { formatMessage } = useIntl();
	const noValueTextExperiment = formatMessage(messages.noValueTextExperiment);
	const { isExperimentEnabled } = useBespokeEmptyFieldTextExperiment();

	const data = useFragment<LabelReadViewFragment>(
		graphql`
			fragment labels_issueFieldLabelsReadviewFull_LabelsReadView on JiraLabelsField {
				fieldId
				selectedLabelsConnection(first: 1000) @required(action: THROW) {
					totalCount
					edges {
						node {
							name
							color {
								colorKey
							}
						}
					}
				}
			}
		`,
		fragmentRef,
	);

	const { selectedLabelsConnection: labels, fieldId } = data;

	if (labels.totalCount !== 0) {
		const labelValues =
			labels?.edges
				?.filter(Boolean)
				.map((edge) => edge.node)
				.filter(
					(
						label,
					): label is {
						name: string;
						color: { colorKey: string | null | undefined } | null | undefined;
					} => label != null && label.name != null,
				) || [];

		if (labelValues.length !== 0) {
			if (isTruncated) {
				return (
					<LabelsTruncatedReadView
						baseSearchUrl={baseSearchUrl}
						fieldId={fieldId}
						labelValues={labelValues}
						isHoverPopoverEnabled={isHoverPopoverEnabled}
					/>
				);
			}

			if (
				!isVisualRefreshEnabled() &&
				(expVal(
					'adding-color-to-labels-field-in-jira-experiment',
					'isColoredLabelsEnabled',
					false,
				) ||
					expVal(
						'adding-color-to-labels-field-experiment-milestone2',
						'isColorfulNewLabelsEnabled',
						false,
					))
			) {
				return (
					<GroupWithNegativeMarginOffset>
						{labelValues.map((label) => (
							<ColoredTag
								key={label.name}
								labelName={label.name}
								href={getLabelHref(fieldId, label.name, baseSearchUrl)}
							/>
						))}
					</GroupWithNegativeMarginOffset>
				);
			}

			return (
				<GroupWithNegativeMarginOffset>
					{labelValues.map((label) => {
						const labelColor = () => {
							if (
								expVal(
									'adding-color-to-labels-field-experiment-milestone2',
									'isColorfulNewLabelsEnabled',
									false,
								)
							) {
								const colorName = label?.color?.colorKey;
								if (!colorName) {
									return memoizeGetAutoGeneratedColorForLabelsTag(label?.name || '');
								}
								const { tagColor } = getColorMetadata(colorName);
								return tagColor;
							}
							if (
								expVal(
									'adding-color-to-labels-field-in-jira-experiment',
									'isColoredLabelsEnabled',
									false,
								)
							) {
								return memoizeGetAutoGeneratedColorForLabelsTag(label?.name || '');
							}
						};

						return (
							<Tag
								key={label.name}
								isRemovable={false}
								text={label.name}
								href={getLabelHref(fieldId, label.name, baseSearchUrl)}
								// @ts-expect-error - TS2322 -  Type 'null' is not assignable to type 'TagColor'.
								color={labelColor()}
							/>
						);
					})}
				</GroupWithNegativeMarginOffset>
			);
		}
	}

	return <EmptyFieldText emptyText={isExperimentEnabled ? noValueTextExperiment : undefined} />;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GroupWithNegativeMarginOffset = styled.div({
	display: 'flex',
	width: '100%',
	flexWrap: 'wrap',
	justifyContent: 'flex-start',
	// Tag has a built-in margin of 4px, so we use a negative side margin to allow tags to align with their parent.
	marginTop: 0,
	marginRight: token('space.negative.050'),
	marginBottom: 0,
	marginLeft: token('space.negative.050'),
});
